import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	HeadContent,
	Scripts,
	useCanGoBack,
	useNavigate,
	useRouter
} from '@tanstack/react-router'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import '@/index.css'
import '@/lib/i18n'
import { Button } from '@repo/ui/button'
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '@repo/ui/card'
import { Toaster } from '@repo/ui/sonner'
import { TooltipProvider } from '@repo/ui/tooltip'
import { formatError, getErrorCode } from '@repo/ui/utils'
import logoMark from '@/assets/logo-mark.png'
import { APP_NAME, type TKey, translateApp } from '@/lib/i18n'
import { PermissionError } from '@/modules/permissions/lib/permission-error'
import type { RouterContext } from '@/router'

export const Route = createRootRouteWithContext<RouterContext>()({
	// Keep the document shell SSR-capable so SSR profiles can render <html>,
	// head tags, request language, and the white page background. This does not
	// force child routes to SSR; routes without their own `ssr` still follow
	// the active profile's `defaultSsr`.
	ssr: true,
	beforeLoad: ({ context, serverContext }) => {
		return {
			appLanguage: serverContext?.appLanguage ?? context.appLanguage
		}
	},
	head: () => ({
		meta: [
			{ charSet: 'utf-8' },
			{ name: 'color-scheme', content: 'only light' },
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1.0'
			},
			{ title: APP_NAME }
		],
		links: [{ rel: 'icon', type: 'image/png', href: '/favicon.png' }]
	}),
	shellComponent: RootDocument
})

function RootDocument({ children }: { children: ReactNode }) {
	const context = Route.useRouteContext()

	return (
		<html
			className='h-full'
			lang={context.appLanguage}
			style={{ colorScheme: 'only light', backgroundColor: '#fff' }}
		>
			<head>
				<HeadContent />
			</head>
			<body className='h-full'>
				<TooltipProvider>
					<Toaster richColors />
					{children}
				</TooltipProvider>
				<Scripts />
			</body>
		</html>
	)
}

export function NotFoundPage() {
	const { t } = useRouteTranslation()
	const navigate = useNavigate()

	return (
		<FallbackLayout
			actions={
				<Button onClick={() => navigate({ to: '/' })}>
					{t('common:app_fallbacks.go_home')}
				</Button>
			}
			description={t('common:app_fallbacks.not_found.description')}
			title={t('common:app_fallbacks.not_found.title')}
		/>
	)
}

export function LoadingPage() {
	const { t } = useRouteTranslation()

	return (
		<div className='grid min-h-full w-full place-items-center p-4'>
			<img
				alt={t('common:app_fallbacks.loading')}
				className='size-28 animate-logo-pulse'
				height={112}
				src={logoMark}
				width={112}
			/>
		</div>
	)
}

export function ErrorPage({ error, reset }: ErrorComponentProps) {
	const { appLanguage, t } = useRouteTranslation()
	const { handleGoBack } = useFallbackNavigation()

	if (error instanceof PermissionError) {
		return (
			<FallbackLayout
				actions={
					<Button onClick={handleGoBack} variant='outline'>
						{t('common:app_fallbacks.go_back')}
					</Button>
				}
				description={t(
					'common:app_fallbacks.permission_error.description'
				)}
				title={t('common:app_fallbacks.permission_error.title')}
			/>
		)
	}

	return (
		<FallbackLayout
			actions={
				<>
					<Button onClick={reset}>
						<RefreshCw data-icon='inline-start' />
						{t('common:app_fallbacks.try_again')}
					</Button>
					<Button onClick={handleGoBack} variant='outline'>
						{t('common:app_fallbacks.go_back')}
					</Button>
				</>
			}
			// A backend error explains itself; any other message would only
			// repeat the title.
			description={
				getErrorCode(error)
					? formatError(error, appLanguage)
					: t('common:app_fallbacks.error.description')
			}
			title={t('common:app_fallbacks.error.title')}
		/>
	)
}

function useRouteTranslation() {
	const context = Route.useRouteContext()
	const { appLanguage } = context

	return {
		appLanguage,
		t: (key: TKey) => translateApp(appLanguage, key)
	}
}

/** Fills the route outlet, not the viewport: fallbacks render nested inside still-mounted parent layouts. */
function FallbackLayout({
	actions,
	description,
	title
}: {
	actions: ReactNode
	description: string
	title: string
}) {
	return (
		<main className='grid min-h-full w-full place-items-center p-6'>
			<Card className='w-full max-w-xl bg-transparent shadow-none ring-0'>
				<CardHeader className='gap-6'>
					<div className='flex size-12 items-center justify-center rounded-lg border border-destructive/20 bg-destructive/10 text-destructive'>
						<TriangleAlert aria-hidden='true' className='size-6' />
					</div>
					<CardTitle className='text-3xl'>{title}</CardTitle>
					<CardDescription className='max-w-lg text-base leading-7'>
						{description}
					</CardDescription>
				</CardHeader>
				<CardFooter className='flex-wrap gap-2'>{actions}</CardFooter>
			</Card>
		</main>
	)
}

function useFallbackNavigation() {
	const canGoBack = useCanGoBack()
	const navigate = useNavigate()
	const router = useRouter()

	return {
		handleGoBack: () => {
			if (canGoBack) {
				router.history.back()
				return
			}

			return navigate({ to: '/' })
		}
	}
}
